import type { LabsCopy } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';

/**
 * Every user-visible string in the Career Explorer lab.
 *
 * 🔴 WRITTEN IN PREVIEW VOICE FROM THE START, unlike the three vertical packs. Those were authored for
 * a subscriber mid-trial and are corrected at runtime by `preview-copy.ts`, because editing them would
 * change the wording of a live experiment. This pack has no sprint audience and never will, so the
 * plain sentences, the honest module count and the absence of em dashes are simply what is written
 * here. The reader is a student who typed a question into Google.
 *
 * 🔴 IT NAMES NO FIELD, ANYWHERE. This lab goes on nursing pages and engineering pages and accounting
 * pages. Every sentence has to work for all of them, which means the copy talks about WORK rather than
 * about an industry. Any string here that could only be true of one field is a bug, not a flourish.
 *
 * The sprint-only blocks (handoff, offer, prepaywallOffer, m4) are unreachable on this surface. They
 * are written plainly rather than left as stubs, because a stub that ships by accident is worse than a
 * short honest sentence that does.
 */
export const EXPLORE_COPY: LabsCopy = {
  // Unreachable: the hand-off modal belongs to the gated sprint, which never runs this pack.
  handoff: {
    artifactTitle: 'Your working style',
    body: 'You just did a short version of the real thing. A full externship is {duration} of the same kind of work on a real company, with feedback. The free trial is where that starts.',
    duration: '7 minutes',
    headline: 'You just finished your first piece of real work',
    trackLabel: 'Career Explorer',
  },
  m1: {
    activity: {
      eyebrow: 'Module 1 · A Day in the Life',
      helper: 'Real things people do at work. Which ones sound like you?',
    },
    complete: { sub: 'Now a few questions about how you think.' },
    intro: {
      accentEm: 'before you have to pick one.',
      accentPre: 'Swipe through {count} of them and find out which half of the work pulls you',
      headline: 'Two kinds of people, in almost every job',
      lead: 'Nearly every job has two halves. Some people work out what is actually going on. Others make the thing actually work. Most students never find out which half suits them until they are already doing it, and by then they have picked a major.',
    },
    results: {
      bridge: {
        emphasis: 'think',
        post: ' when things get messy. Four quick questions, then your result.',
        pre: 'Next, a few questions about how you ',
      },
      interpretation: {
        moderateA:
          'You lean towards working out what is going on. You want the picture to be clear before anyone commits to something.',
        moderateB:
          'You lean towards making things work. You would rather be moving on a decent answer than still refining a perfect one.',
        split:
          'You are genuinely split. Both halves pulled you, which is less common than it sounds and worth paying attention to.',
        strongA:
          'You are drawn hard to the diagnosis half. Finding the real problem is the part of the job you would actually enjoy.',
        strongB:
          'You are drawn hard to the delivery half. Making something work, and keeping it working, is where you want to be.',
      },
      // The honest line when a reader swiped right on nothing. Without it the engine reports a dead-even
      // 50/50 and tells someone who rejected all eight that they are "genuinely split", on the one
      // screen whose whole job is to prove the product was paying attention.
      noneChosen: 'None of those landed, which is a real answer. The next few questions get at it a different way.',
    },
  },
  m2: {
    complete: { sub: 'One more module, then your result.' },
    intro: {
      headline: 'How do you think when it gets messy?',
      lead: 'Four situations with no obviously right answer. Both ends are things a good colleague genuinely does, so go with your instinct rather than the one that sounds better.',
      points: [
        'There is no correct answer to any of these.',
        'Go with what you would actually do, not what sounds impressive.',
        'It takes about a minute.',
      ],
    },
    results: { footer: 'This shapes the result you get at the end.' },
  },
  m3: {
    activity: {
      diagnosisHeading: 'What is actually going wrong here?',
      diagnosisPlaceholder: 'Volume tripled and quality fell at the same time.',
      diagnosisSample:
        'Requests more than tripled while the share solved on the first try fell from 90% to 80%. Every request that is not solved first time comes back, which is why repeat contacts grew faster than volume did.',
      eyebrow: 'Module 3 · Read the numbers',
      feedbackCorrect: 'That is right.',
      feedbackWrong: 'Not quite. Check which column and which row you are dividing by.',
      metricsHeading: 'Work out three rates',
      metricsHelper: 'Use the table. Each one is a single division.',
      revealBody:
        'The topline more than tripled. Every single quality measure got worse over the same period. This operation is not straining under growth, it is being outrun by it, and no amount of extra effort inside the current process fixes that.',
      revealLabel: 'What the numbers say:',
      subhead: 'Three years of a support operation',
      tableTag: 'Practice example',
    },
    complete: { sub: 'That is the work. Here is your result.' },
    intro: {
      chips: ['About 2 minutes', 'No maths background needed'],
      headline: 'Now the actual work',
      subtitle: 'Everything so far was about you. This part is a real task.',
    },
    primer: {
      body: 'A table like this always answers the same question: is the thing getting better or worse per unit of work. Not "did the number go up", which almost always says yes, but whether each extra request was handled as well as the last one.',
      eyebrow: 'Before you start',
      headline: 'How to read an operating table',
      pullQuote: 'A total tells you how big. A rate tells you how well.',
      rows: [
        { hint: 'How much came in', label: 'Requests', operator: '' },
        { hint: 'How much was handled properly first time', label: 'Solved First Time', operator: '÷' },
        { hint: 'The share that came back anyway', label: 'Repeat Contacts', operator: '=' },
      ],
    },
  },
  // Unreachable: M4 is not in the preview's step sequence and this pack runs nowhere else.
  m4: {
    activity: {
      feedbackWrong: 'Look at the trend again before you commit to that.',
      nameHeading: 'Your name',
      pickHeading: 'Your read',
      pickHelper: 'One pick per row.',
      sampleFooter: 'Yours does not have to match this.',
      sampleHeading: 'A sample analysis',
      sampleHelper: 'One way of reading the same table.',
      samplePanelLabel: 'Sample analysis · curated example',
      sampleRunCta: 'Run analysis',
      submitCta: 'See my analysis',
      takeHeading: 'Where do you disagree?',
      takeHelper: 'Push back on the sample if you read it differently.',
      takePlaceholder: 'I would look at it differently because...',
    },
    intro: {
      body: [
        'You have read one operation. Now read another and say what you would do about it.',
        'There is no answer key for the second half. That is the point.',
      ],
      headline: 'Read it for yourself',
    },
    selection: {
      eyebrow: 'Pick one',
      headline: 'Pick an operation',
      subhead: 'Whichever one you would rather understand.',
    },
  },
  // Unreachable: the entry offer is pitched inside the product, where this pack is not registered.
  offer: {
    body: 'Two halves to almost every job. Five minutes to find out which one is you.',
    eyebrow: 'Career Explorer',
    headlineLead: 'Stop guessing which career fits you.',
    headlineTail: 'Spend 5 minutes finding out.',
    tilePromise: 'Read a real operation and say what is going wrong.',
  },
  // Unreachable: same, on the pre-paywall surface.
  prepaywallOffer: {
    body: 'A short piece of real work, before you decide anything.',
    eyebrow: 'Career Explorer',
    headlineLead: 'Try the real work, free,',
    headlineTail: 'before you pay.',
  },
  recommendations: {
    archLineNoSubject: "You're {archetype}. Here's where that takes you next.",
    archLineWithSubject: "You're {archetype}. Here's where your {subject} analysis takes you next.",
  },
  welcome: {
    cta: 'Begin',
    eyebrow: 'Career Explorer',
    // The preview welcome replaces the tail and the lead with its own, and personalises this lead from
    // `?src=` where it can. This is the fallback a reader sees on a page we cannot name a subject for.
    headlineLead: 'What kind of work are you actually good at?',
    headlineTail: 'Spend 5 minutes finding out.',
    lead: 'Two halves to almost every job. See which one is you.',
    metaDuration: 'About 5 minutes',
    steps: [
      { sub: 'Real things people do at work', title: 'Sort the work' },
      { sub: 'Quick reads reveal how you think', title: 'Answer four questions' },
      { sub: 'Read a real operation and find what is going wrong', title: 'Do the actual work' },
    ],
  },
};
